import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Loader, AlertCircle, CheckCircle, ArrowLeft, Zap, ShieldCheck, MessageCircle, ChevronRight } from 'lucide-react';
import api from '../services/api';
import BrandLogo from '../components/BrandLogo';
import Seo from '../components/Seo';

function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError('Enter a valid work email');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to dispatch protocol.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-[#F3F3F3] px-4 py-8">
      <Seo
        title="Forgot Password | WaapLeads CRM"
        description="Request a secure WaapLeads CRM password reset link to restore access to your workspace."
        pathname="/forgot-password"
      />
      {/* ── RIGHT SIDE: FORGOT FORM ── */}
      <div className="w-full max-w-md bg-white border border-[#E5E5E5] rounded-lg p-6 sm:p-8 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="w-full">
          {sent ? (
            <div className="text-center space-y-8 py-4 animate-nebula-fade-up">
              <div className="w-20 h-20 mx-auto bg-[#EBF7E6] border border-[#CDEFC4] rounded-lg flex items-center justify-center shadow-sm shadow-green-500/10 mb-8">
                 <CheckCircle size={40} className="text-[#2E844A]" />
              </div>
              <div className="space-y-3">
                 <h2 className="text-xl font-bold text-[#2E2E2E]">Check Your Email</h2>
                 <p className="text-[13px] text-[#5C5C5C] font-medium leading-relaxed max-w-xs mx-auto">
                   If <strong>{email}</strong> is registered, a secure reset link will synchronize with your inbox shortly.
                 </p>
              </div>
              <Link to="/login" className="w-full flex items-center justify-center gap-3 h-9 bg-white border border-[#C9C9C9] text-[#0176D3] rounded text-[13px] font-semibold hover:bg-[#F3F2F2]">
                <ArrowLeft size={16} /> Back to Login
              </Link>
            </div>
          ) : (
            <>
              <div className="flex flex-col items-center mb-6 text-center">
                <BrandLogo size={40} showText={false} className="mb-4" />
                <h1 className="text-xl font-bold text-[#2E2E2E] mb-1">Reset Password</h1>
                <p className="text-[13px] text-[#5C5C5C] font-medium">We will email you a reset link</p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-[#FEF1EE] border border-[#FDDDE3] rounded flex items-center gap-3 text-[#BA0517] text-[13px] font-semibold">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="space-y-3 slds-input-group">
                  <label className="text-[11px] font-semibold text-[#706E6B]">Work Email</label>
                  <div className="relative group">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B] group-focus-within:text-[var(--slds-color-brand)] transition-colors" size={16} />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(''); }}
                      className="pl-10 w-full h-9 border border-[#D8DDE6] rounded text-[13px] focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none"
                      placeholder="name@company.com"
                      autoFocus
                    />
                  </div>
                </div>

                <button 
                  type="submit" 
                  className="w-full flex items-center justify-center gap-3 h-9 bg-[#0176D3] hover:bg-[#014486] text-white rounded text-[13px] font-semibold disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader size={20} className="animate-spin" />
                  ) : (
                    <>
                      <span>Dispatch Recovery</span>
                      <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center pt-4 border-t border-[#E5E5E5]">
                <Link to="/login" className="inline-flex items-center gap-2 text-[12px] font-bold text-[#706E6B] hover:text-[#2E2E2E] transition-colors">
                  <ArrowLeft size={14} /> Back to Login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
